import { useCallback, useState } from 'react';
import { useWidget } from '../WidgetProvider';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Upload, X, Camera, ImageIcon, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { convertToJpeg } from '../utils/convertImage';

const MAX_IMAGES = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

export function ImageUploadStep() {
  const { state, addImageFile, removeImageFile, nextStep, trackEvent } = useWidget();
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isConverting, setIsConverting] = useState(false);

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files) return;
      setError(null);

      const currentCount = state.imageFiles.length;
      const remaining = MAX_IMAGES - currentCount;

      if (remaining <= 0) {
        setError('Maximum 3 images allowed');
        return;
      }

      const filesToAdd = Array.from(files).slice(0, remaining);
      setIsConverting(true);

      try {
        for (const file of filesToAdd) {
          if (!ACCEPTED_TYPES.includes(file.type)) {
            setError('Please upload JPG, PNG, or WebP images only');
            continue;
          }
          if (file.size > MAX_FILE_SIZE) {
            setError('Images must be under 10MB');
            continue;
          }
          // Convert all non-JPEG images (HEIC, PNG, WebP) to JPEG for API compatibility
          const converted = await convertToJpeg(file);
          addImageFile(converted);
        }
      } finally {
        setIsConverting(false);
      }
    },
    [state.imageFiles.length, addImageFile]
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const canContinue = state.imageFiles.length > 0 && !isConverting;

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-lg font-medium">Upload room photos</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Add 1-3 photos of your {state.roomType?.replace('_', ' ')} from different angles
        </p>
      </div>

      {/* Upload Zone */}
      <div
        className={cn(
          'relative border-2 border-dashed rounded-lg p-8 text-center transition-colors',
          dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25',
          state.imageFiles.length >= MAX_IMAGES && 'opacity-50 pointer-events-none'
        )}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept={ACCEPTED_TYPES.join(',')}
          multiple
          onChange={handleInputChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={state.imageFiles.length >= MAX_IMAGES}
        />
        <div className="flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
            <Upload className="w-7 h-7 text-muted-foreground" />
          </div>
          <div>
            <p className="font-medium">Drop photos here or tap to upload</p>
            <p className="text-xs text-muted-foreground mt-1">
              JPG, PNG, WebP up to 10MB each
            </p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {/* Image Previews */}
      {state.imageFiles.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {state.imageFiles.map((file, index) => (
            <Card key={index} className="relative aspect-square overflow-hidden">
              <img
                src={URL.createObjectURL(file)}
                alt={`Upload ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => removeImageFile(index)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </Card>
          ))}

          {/* Empty slots */}
          {Array.from({ length: MAX_IMAGES - state.imageFiles.length }).map((_, index) => (
            <Card
              key={`empty-${index}`}
              className="aspect-square flex items-center justify-center bg-muted/50 border-dashed"
            >
              <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
            </Card>
          ))}
        </div>
      )}

      {/* Tips */}
      <Card className="p-4 bg-muted/50">
        <h3 className="font-medium text-sm flex items-center gap-2">
          <Camera className="w-4 h-4" />
          Photo Tips
        </h3>
        <ul className="mt-2 text-xs text-muted-foreground space-y-1">
          <li>• Take photos in good natural lighting</li>
          <li>• Capture the full room from corner to corner</li>
          <li>• Include close-ups of areas you want to change</li>
        </ul>
      </Card>

      {/* Continue Button */}
      <Button
        className="w-full h-12 text-base font-semibold bg-accent hover:bg-accent/90 text-accent-foreground shadow-md shadow-accent/20 transition-all hover:shadow-lg hover:shadow-accent/25 active:scale-[0.98] disabled:opacity-50 disabled:shadow-none"
        size="lg"
        disabled={!canContinue}
        onClick={() => {
          trackEvent('images_uploaded', { image_count: state.imageFiles.length });
          nextStep();
        }}
      >
        Continue with {state.imageFiles.length} photo{state.imageFiles.length !== 1 ? 's' : ''}
      </Button>
    </div>
  );
}
